import loggerService from '@/services/logger.service';

/**
 * The animated slides deck is rendered by a third-party player (DopPresentation) inside a
 * same-origin iframe. It does not paint into a single canvas: shapes land on `<canvas>` layers,
 * pictures on `<img>`, and text on absolutely positioned `<pre>`/`<span>` nodes inside `#resizer`.
 * `captureStream()` therefore has nothing to attach to, so we re-draw that subtree onto our own
 * canvas every frame and capture that instead.
 */
const PLAYER_WRAPPER_SELECTOR = '.new-slide-animations-player iframe';
const SLIDE_ROOT_ID = 'resizer';
const MIN_VISIBLE_ALPHA = 0.01;

// CORS proxy copies of cross-origin slide images. Drawing a cross-origin <img> without
// crossOrigin="anonymous" taints the canvas and silently kills captureStream(), so each
// such element gets one CORS-enabled copy that is drawn in its place. Keyed on the DOM
// <img> element so entries are GC'd automatically when the player removes a slide image
// from the DOM — no manual cleanup needed.
const corsImagesCache = new WeakMap<HTMLImageElement, HTMLImageElement>();

export function getAnimatedSlidesIframe(): HTMLIFrameElement | null {
  return document.querySelector<HTMLIFrameElement>(PLAYER_WRAPPER_SELECTOR);
}

/**
 * Resolves the element the player renders the current slide into, or null while the player is
 * still booting. `#resizer` exists in the static markup with a placeholder size, so the only
 * reliable readiness signal is it having children.
 */
export function getAnimatedSlidesRoot(): HTMLElement | null {
  const iframe = getAnimatedSlidesIframe();
  if (!iframe) {
    return null;
  }

  let root: HTMLElement | null = null;
  try {
    root = iframe.contentDocument?.getElementById(SLIDE_ROOT_ID) ?? null;
  } catch (error) {
    loggerService.error('[Local Recording] Animated slides: cannot reach the player document', error);
    return null;
  }

  if (!root?.childElementCount) {
    return null;
  }

  const rect = root.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 ? root : null;
}

export function waitForAnimatedSlidesPlayer(timeoutMs: number, pollIntervalMs = 250): Promise<HTMLElement> {
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;

    const poll = () => {
      const root = getAnimatedSlidesRoot();
      if (root) {
        resolve(root);
        return;
      }
      if (Date.now() >= deadline) {
        reject(new Error(`Animated slides player did not render within ${timeoutMs}ms`));
        return;
      }
      setTimeout(poll, pollIntervalMs);
    };

    poll();
  });
}

/**
 * Composites the current state of the player subtree onto `canvasElement`, letterboxed to keep
 * the slide's aspect ratio.
 *
 * @param canvasElement - The offscreen canvas backing the published screen-share track
 * @returns true when a frame was painted, false when the player is not renderable right now
 */
export function drawAnimatedSlideFrame(canvasElement: HTMLCanvasElement) {
  const imageCache = corsImagesCache;
  const root = getAnimatedSlidesRoot();
  const ctx = canvasElement?.getContext('2d');
  if (!root || !ctx) {
    return false;
  }

  const win = root.ownerDocument.defaultView;
  if (!win) {
    return false;
  }

  const rootRect = root.getBoundingClientRect();
  const scale = Math.min(canvasElement.width / rootRect.width, canvasElement.height / rootRect.height);
  const originX = (canvasElement.width - rootRect.width * scale) / 2;
  const originY = (canvasElement.height - rootRect.height * scale) / 2;

  ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);
  ctx.globalAlpha = 1;
  // Never varies per element, so it is set once a frame rather than per text run.
  ctx.textBaseline = 'top';

  const spacedCtx = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  const supportsLetterSpacing = 'letterSpacing' in spacedCtx;

  try {
    const rootChildren = root.children;
    for (let i = 0, len = rootChildren.length; i < len; i++) {
      paintElement(rootChildren[i] as HTMLElement, 1);
    }
  } catch (error) {
    loggerService.error('[Local Recording] Animated slides: error compositing frame', error);
    return false;
  } finally {
    ctx.globalAlpha = 1;
  }

  return true;

  // Depth-first in document order, which matches the player's paint order: every node is
  // absolutely positioned and none of them set z-index.
  function paintElement(element: HTMLElement, inheritedAlpha: number) {
    // Pre-flight via inline styles: avoids getComputedStyle for invisible subtrees without
    // forcing a layout recalculation. Slide transitions set opacity: 0 inline on .DC0/.DC1,
    // so this eliminates the layout-forcing call for the entire outgoing-slide subtree each frame.
    const inlineStyle = element.style;
    if (inlineStyle.display === 'none' || inlineStyle.visibility === 'hidden') {
      return;
    }
    const rawInlineOpacity = inlineStyle.opacity;
    if (rawInlineOpacity !== '') {
      const inlineOpacity = Number.parseFloat(rawInlineOpacity);
      if (!Number.isNaN(inlineOpacity) && inheritedAlpha * inlineOpacity <= MIN_VISIBLE_ALPHA) {
        return;
      }
    }

    const style = win!.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return;
    }

    // Slide transitions are driven purely by opacity (.DC0/.DC1), so an invisible subtree here is
    // the slide we just moved away from - drawing it would double-expose the frame.
    const ownOpacity = rawInlineOpacity !== '' ? Number.parseFloat(rawInlineOpacity) : Number.parseFloat(style.opacity);
    const alpha = inheritedAlpha * (Number.isNaN(ownOpacity) ? 1 : ownOpacity);
    if (alpha <= MIN_VISIBLE_ALPHA) {
      return;
    }

    drawElement(element, style, alpha);

    // `children` is live, but the walk is synchronous and only reads, so nothing can mutate the
    // player document mid-iteration. Iterating it directly saves an array allocation per element
    // per frame - the most-allocated thing in this function.
    const children = element.children;
    for (let i = 0, len = children.length; i < len; i++) {
      paintElement(children[i] as HTMLElement, alpha);
    }
  }

  function drawElement(element: HTMLElement, style: CSSStyleDeclaration, alpha: number) {
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return;
    }

    const x = originX + (rect.left - rootRect.left) * scale;
    const y = originY + (rect.top - rootRect.top) * scale;
    const width = rect.width * scale;
    const height = rect.height * scale;

    ctx!.globalAlpha = alpha;

    if (isPaintedBackground(style.backgroundColor)) {
      ctx!.fillStyle = style.backgroundColor;
      ctx!.fillRect(x, y, width, height);
    }

    if (element instanceof win!.HTMLCanvasElement) {
      ctx!.drawImage(element, x, y, width, height);
      return;
    }

    if (element instanceof win!.HTMLImageElement) {
      drawImageElement(element, x, y, width, height);
      return;
    }

    if (element instanceof win!.HTMLVideoElement) {
      if (element.readyState >= 2) {
        ctx!.drawImage(element, x, y, width, height);
      }
      return;
    }

    if (element.tagName === 'svg' || element instanceof win!.SVGElement) {
      loggerService.warn('[Local Recording] Animated slides: unhandled SVG element in compositor', element.tagName);
      return;
    }

    drawDirectText(element, style, rect, x, y);
  }

  /**
   * The player loads slide pictures from the CDN without a crossOrigin attribute. Drawing those
   * directly would taint our canvas and silently kill `captureStream()`, so we mirror them through
   * a CORS-enabled copy (the CDN already serves the headers - the static slide pipeline relies on
   * the same trick) and skip the picture until that copy has loaded.
   *
   * A mirror that fails to load is kept in the cache so the request is not retried every frame;
   * the picture is then missing from the recording, which is why the failure is logged. Falling
   * back to the original is not an option - that taints the canvas and kills the whole stream.
   */
  function drawImageElement(image: HTMLImageElement, x: number, y: number, width: number, height: number) {
    if (!image.complete || !image.naturalWidth) {
      return;
    }

    const source = image.currentSrc || image.src;
    if (!source) {
      return;
    }

    const mirrored = imageCache.get(image);
    if (mirrored) {
      if (mirrored.complete && mirrored.naturalWidth) {
        ctx!.drawImage(mirrored, x, y, width, height);
      }
      return;
    }

    if (image.crossOrigin || isSameOrigin(source, root!.ownerDocument.baseURI)) {
      ctx!.drawImage(image, x, y, width, height);
      return;
    }

    const corsImage = new Image();
    corsImage.crossOrigin = 'anonymous';
    corsImage.onerror = () => {
      loggerService.error(
        '[Local Recording] Animated slides: CORS copy of a slide picture failed to load, it will be missing from the recording',
        source
      );
    };
    imageCache.set(image, corsImage);
    corsImage.src = source;
  }

  /**
   * Slide text is real DOM text, not canvas output, so it has to be re-drawn glyph by glyph.
   * Only direct text children are drawn, at the owning element's own box, so text split across
   * per-run `<span>`s stays positioned.
   */
  function drawDirectText(element: HTMLElement, style: CSSStyleDeclaration, rect: DOMRect, x: number, y: number) {
    let text = '';
    const childNodes = element.childNodes;
    for (let i = 0, len = childNodes.length; i < len; i++) {
      const node = childNodes[i];
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent ?? '';
      }
    }
    if (!text.trim()) {
      return;
    }

    // The player scales text with a CSS transform on the run itself; offsetHeight is the
    // untransformed layout height, so their ratio recovers that scale.
    const layoutHeight = element.offsetHeight;
    const transformScale = layoutHeight > 0 ? rect.height / layoutHeight : 1;
    const fontScale = transformScale * scale;

    const parsedFontSize = Number.parseFloat(style.fontSize);
    const fontSize = parsedFontSize * fontScale;
    if (!fontSize) {
      return;
    }

    ctx!.font = `${style.fontStyle} ${style.fontWeight} ${fontSize}px ${style.fontFamily}`;
    ctx!.fillStyle = style.color;

    // Writing letterSpacing invalidates the canvas font state in Blink, so a run with no tracking -
    // the common case - skips both the set and the reset. The reset always restores 0px, so the
    // context is guaranteed to already be at 0px when the write is skipped.
    let spacing = 0;
    if (supportsLetterSpacing) {
      const parsedSpacing = Number.parseFloat(style.letterSpacing);
      spacing = Number.isNaN(parsedSpacing) ? 0 : parsedSpacing * fontScale;
      if (spacing !== 0) {
        spacedCtx.letterSpacing = `${spacing}px`;
      }
    }

    // Most runs are a single line, and splitting allocates an array per text node per frame.
    // lineHeight is only needed to stack lines, so its computed-style read is deferred with it.
    if (text.indexOf('\n') === -1) {
      ctx!.fillText(text, x, y);
    } else {
      const parsedLineHeight = Number.parseFloat(style.lineHeight);
      const lineHeight = (Number.isNaN(parsedLineHeight) ? parsedFontSize * 1.2 : parsedLineHeight) * fontScale;
      const lines = text.split('\n');
      for (let i = 0, len = lines.length; i < len; i++) {
        if (lines[i]) {
          ctx!.fillText(lines[i], x, y + i * lineHeight);
        }
      }
    }

    if (spacing !== 0) {
      spacedCtx.letterSpacing = '0px';
    }
  }
}

function isPaintedBackground(backgroundColor: string): boolean {
  return (
    !!backgroundColor &&
    backgroundColor !== 'transparent' &&
    !backgroundColor.replace(/\s/g, '').startsWith('rgba(0,0,0,0)')
  );
}

function isSameOrigin(source: string, baseUrl: string): boolean {
  try {
    return new URL(source, baseUrl).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Verifies the canvas is still origin-clean. A tainted canvas makes Chrome stop producing frames
 * on an already-captured stream without raising anything, so this is checked once up front.
 */
export function isCanvasCapturable(canvasElement: HTMLCanvasElement): boolean {
  try {
    canvasElement.getContext('2d')?.getImageData(0, 0, 1, 1);
    return true;
  } catch (error) {
    loggerService.error('[Local Recording] Animated slides: canvas is tainted, cannot capture', error);
    return false;
  }
}
